import React, { useState, useEffect } from "react";
import { getInvoices, getInvoiceById, updatePaymentStatus } from "../../services/orderService";
import { useNavigate } from "react-router-dom";
import InvoiceDetailsModal from "./InvoiceDetailsModal";
import "./InvoiceList.css";
import OrderCreate from "./OrderCreate";

const InvoiceList = () => {
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const itemsPerPage = 5;
    const navigate = useNavigate();

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            const response = await getInvoices();
            setInvoices(response.data.map(invoice => ({
                ...invoice,
                total_amount: parseFloat(invoice.total_amount || 0),
                discount: parseFloat(invoice.discount || 0),
                final_amount: parseFloat(invoice.final_amount || 0),
            })));
        } catch (error) {
            console.error("Error fetching invoices:", error);
            setInvoices([]);
        } finally {
            setIsLoading(false);
        }
    };

    // const openInvoiceDetails = async (invoiceId) => {
    //     try {
    //         const response = await getInvoiceById(invoiceId);
    //         setSelectedInvoice(response.data);
    //         setIsDetailsModalOpen(true);
    //     } catch (error) {
    //         console.error("Error fetching invoice details:", error);
    //     }
    // };

    const openInvoiceDetails = async (invoiceId) => {
        try {
            const response = await getInvoiceById(invoiceId);
            const invoiceData = response.data;
    
            setSelectedInvoice({
                ...invoiceData,
                items: invoiceData.items || []  // Ensure items are included
            });
    
            setIsDetailsModalOpen(true);
        } catch (error) {
            console.error("Error fetching invoice details:", error);
        }
    };
    

    const handleRowClick = (e, invoiceId) => {
        if (e.target.tagName === "BUTTON") return;
        openInvoiceDetails(invoiceId);
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await updatePaymentStatus(id, newStatus);
            fetchInvoices();
        } catch (error) {
            console.error("Error updating payment status:", error);
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const filteredInvoices = invoices
        .filter((invoice) => {
            const invoiceIdMatch = invoice.invoice_number?.toString().toLowerCase().includes(searchQuery.toLowerCase());
            const customerNameMatch = `${invoice.FirstName || ''} ${invoice.LastName || ''}`.toLowerCase().includes(searchQuery.toLowerCase());
            const customerIdMatch = invoice.customer_id?.toString().toLowerCase().includes(searchQuery.toLowerCase());

            return invoiceIdMatch || customerNameMatch || customerIdMatch;
        })
        .filter((invoice) => (filterStatus ? invoice.payment_status === filterStatus : true))
        .sort((a, b) => {
            if (sortBy === "price") return b.total_amount - a.total_amount;
            if (sortBy === "date") return new Date(b.order_date) - new Date(a.order_date);
            if (sortBy === "value") return b.total_amount * b.quantity - a.total_amount * a.quantity;
            return 0;
        });

    const totalPages = filteredInvoices.length > 0 ? Math.ceil(filteredInvoices.length / itemsPerPage) : 1;
    const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterStatus, sortBy]);

    return (
        <div className="invoice-container">
            <div className="invoice-controls">
                <input
                    type="text"
                    placeholder="Search by Invoice ID, Customer Name or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">All Payment Status</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="">Sort By</option>
                    <option value="price">Total Price</option>
                    <option value="date">Date</option>
                    <option value="value">Total Value</option>
                </select>
                <button onClick={() => setIsInvoiceModalOpen(true)} className="create-invoice-btn">
                    + Create Invoice
                </button>
            </div>

            {isInvoiceModalOpen && (
                <OrderCreate
                    isOpen={isInvoiceModalOpen}
                    onClose={() => {
                        setIsInvoiceModalOpen(false);
                        fetchInvoices(); // Refresh after creating invoice
                    }}
                />
            )}

            <div className="invoice-table">
                <table>
                    <thead>
                    <tr>
                        <th>Invoice ID</th>
                        <th>Customer</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan="6" className="loading-message">Loading invoices...</td>
                        </tr>
                    ) : paginatedInvoices.length > 0 ? (
                        paginatedInvoices.map((invoice) => (
                            <tr key={invoice.id} onClick={(e) => handleRowClick(e, invoice.id)}>
                                <td>{invoice.invoice_number}</td>
                                <td>
                                    {invoice.FirstName && invoice.LastName
                                        ? `${invoice.FirstName} ${invoice.LastName}`
                                        : `Customer #${invoice.customer_id}`
                                    }
                                </td>
                                <td>Rs.{parseFloat(invoice.total_amount).toFixed(2)}</td>
                                <td>
                                        <span className={invoice.payment_status === "Completed" ? "status-paid" : "status-pending"}>
                                            {invoice.payment_status}
                                        </span>
                                </td>
                                <td>{formatDate(invoice.invoice_date)}</td>
                                <td>
                                    <button
                                        className="update-status-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStatusChange(invoice.id, "Completed");
                                        }}
                                        disabled={invoice.payment_status === "Completed"}
                                    >
                                        {invoice.payment_status === "Completed" ? "Paid" : "Mark as Paid"}
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className="no-records">No invoices found matching your criteria</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {filteredInvoices.length > 0 && (
                <div className="pagination">
                    <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                        ← Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next →
                    </button>
                </div>
            )}

            {isDetailsModalOpen && selectedInvoice && (
                <InvoiceDetailsModal
                    invoice={selectedInvoice}
                    onClose={() => setIsDetailsModalOpen(false)}
                />
            )}
        </div>
    );
};

export default InvoiceList;